// Central content inventory for Easylease Global Resources Limited (EGRL)
// Do not invent facts. All content below is derived from the uploaded company profile.

export const COMPANY = {
  legalName: 'Easylease Global Resources Limited',
  shortName: 'EasyLease',
  acronym: 'EGRL',
  rc: 'RC 7171932',
  tagline: 'Structured equipment leasing for Nigeria’s builders.',
  established: 2023,
  operationalSince: 'October 2023',
  headOffice: 'Easylease Yard, Adjacent The Bahamas by Bilaad, Maitama 2, FCT Abuja',
  email: 'info@easyleasenigeria.com',
  phone: '+234 802 988 0256',
  website: 'www.easyleasenigeria.com',
  officeHours: 'Monday – Friday, 08:00 – 18:00 WAT',
  sectors: ['Construction', 'Real Estate', 'Transportation & Logistics', 'Infrastructure Development'],
  socials: [
    { label: 'LinkedIn', href: '#' },
    { label: 'Instagram', href: '#' },
    { label: 'X (Twitter)', href: '#' },
  ],
};

export const STATS = [
  { value: 43, suffix: '+', label: 'Assets deployed on live projects' },
  { value: 4, suffix: '', label: 'Governance committees' },
  { value: 4, suffix: '', label: 'Sectors served' },
  { value: 2023, suffix: '', label: 'Year established', isYear: true },
];

// Map of authentic uploaded photos to service categories
export const IMAGES = {
  scaffold: Array.from({ length: 15 }, (_, i) => `/media/projects/image${i + 1}.jpeg`),
  acrow: Array.from({ length: 5 }, (_, i) => `/media/projects/image${i + 16}.jpeg`),
  columns: Array.from({ length: 6 }, (_, i) => `/media/projects/image${i + 21}.jpeg`),
  formworks: Array.from({ length: 6 }, (_, i) => `/media/projects/image${i + 27}.jpeg`),
  containers: Array.from({ length: 11 }, (_, i) => `/media/projects/image${i + 33}.jpeg`),
};

export const HERO_IMAGE = '/media/projects/image10.jpeg';
export const ABOUT_IMAGE = '/media/projects/image28.jpeg';
export const CAREERS_IMAGE = '/media/projects/image33.jpeg';
export const CONTACT_IMAGE = '/media/projects/image27.jpeg';

export const SERVICES = [
  {
    slug: 'scaffold-services',
    title: 'Scaffold Services',
    kicker: '01',
    tagline: 'Certified access systems for mid- and high-rise construction.',
    summary: 'Ring-lock, cuplock and tube-and-fitting scaffold pools — mobilised, erected and dismantled by trained crews under strict PPE protocols.',
    benefits: [
      'Certified access solutions engineered for Nigerian site conditions',
      'Trained erection & dismantling crews included in the lease',
      'Preventive-maintenance cycle and inspection log per asset',
      'Flexible short- and long-tenure leasing terms'
    ],
    detail: [
      'Our scaffold service line delivers structured, safety-compliant access for concrete, cladding, and finishing works across residential, commercial, and infrastructure builds.',
      'Assets are pool-managed with a preventive-maintenance cycle, and each mobilisation is accompanied by trained operatives operating under a formal PPE regime.'
    ],
    images: IMAGES.scaffold,
  },
  {
    slug: 'acrow-props',
    title: 'Acrow Props',
    kicker: '02',
    tagline: 'Heavy-duty adjustable props for slab and beam formwork support.',
    summary: 'Load-rated Acrow props sized 0–4, deployable in dense arrays for suspended slab and beam formwork — supplied in campaign-ready quantities.',
    benefits: [
      'Load-rated Sizes 0 through 4',
      'Campaign-ready quantities for multi-storey pours',
      'Threaded jack plates and locking pins inspected per cycle',
      'Fast site mobilisation across the FCT and neighbouring states'
    ],
    detail: [
      'Acrow props are the workhorse of any structural slab pour. EGRL maintains a deep pool of load-rated units, tagged and pressure-tested on rotation.',
      'Deployment is coordinated with our formworks team to compress your critical-path days.'
    ],
    images: IMAGES.acrow,
  },
  {
    slug: 'metal-columns',
    title: 'Metal Columns',
    kicker: '03',
    tagline: 'Adjustable steel column formwork for repeatable, plumb structural pours.',
    summary: 'Modular steel column moulds for square and rectangular columns — fast to strike, dimensionally accurate, and reusable across cycles.',
    benefits: [
      'Modular adjustable dimensions',
      'Faster strike times vs. site-built timber alternatives',
      'Dimensional accuracy for repeatable structural quality',
      'High cycle count reduces per-pour cost'
    ],
    detail: [
      'Steel column moulds significantly improve pour quality and site productivity compared to on-site timber solutions.',
      'EGRL’s columns are maintained on a defined refurbishment schedule and supplied with all required tie-rods, wedges and access brackets.'
    ],
    images: IMAGES.columns,
  },
  {
    slug: 'formworks',
    title: 'Formworks',
    kicker: '04',
    tagline: 'Slab, beam and wall formwork systems for suspended reinforced-concrete works.',
    summary: 'Full formwork solutions: H-frame shoring, ply-faced deck systems, wall panels and beam boxes — supported by an engineering-aware coordination team.',
    benefits: [
      'Complete slab, beam and wall systems',
      'Engineered layouts on request',
      'Compatible with Acrow-prop and shoring pools',
      'Scaled to residential, commercial and infrastructure projects'
    ],
    detail: [
      'Formwork is the DNA of a good concrete structure. EGRL provides fully integrated formwork lease packages compatible with our prop and shoring pools.',
      'Layout, mobilisation and post-strike inspection are handled as one coordinated service scope.'
    ],
    images: IMAGES.formworks,
  },
  {
    slug: 'office-storage-containers',
    title: 'Office & Storage Containers',
    kicker: '05',
    tagline: 'Furnished and unfurnished 20ft & 40ft containers for site offices and secure storage.',
    summary: 'Refurbished ISO containers converted into professional site offices, meeting rooms and lockable storage — delivered, sited and connected on schedule.',
    benefits: [
      '20ft and 40ft footprints (furnished / unfurnished)',
      'Site-office fit-outs — desks, HVAC, lighting on request',
      'Lockable, weather-tight storage variants',
      'Mobilisation, siting, and demobilisation included'
    ],
    detail: [
      'Our containerised site offices are engineered for real Nigerian site conditions — dust, sun, downpours and long project durations.',
      'Storage variants are lockable, weather-tight and pest-managed, protecting your consumables and high-value site inventory.'
    ],
    images: IMAGES.containers,
  },
];

export const CORE_VALUES = [
  { title: 'Integrity', body: 'We uphold trust through transparency, accountability and ethical conduct.' },
  { title: 'Efficiency', body: 'We deliver valuable services in a cost-effective and timely manner.' },
  { title: 'Innovation', body: 'We create flexible, mobile, scalable and smart asset solutions for dynamic construction needs.' },
  { title: 'Customer-Centricity', body: 'We listen, adapt, and deliver to match each client’s project goals — prioritising the needs of our clients at every step.' },
  { title: 'Impact', body: 'We measure success not just in transactions, but in how we contribute to growth, resilience and sustainability.' },
];

export const MILESTONES = [
  { year: '2023', title: 'Incorporation', body: 'Easylease Global Resources Limited incorporated as a Nigerian asset-leasing and logistics support company.' },
  { year: 'Oct 2023', title: 'Operational Launch', body: 'Commercial operations commence from the head-office yard in Maitama 2, FCT Abuja.' },
  { year: '2024', title: 'Board Formation', body: 'Board convened under Hajiya Sa’adatu Musa Dangoggo Aliyu with five permanent committees.' },
  { year: '2024', title: 'Asset Build-Out', body: 'Structured expansion across scaffolding, acrow props, metal columns, formworks and containerised site infrastructure.' },
  { year: '2025', title: 'ELRA Compliance Track', body: 'Registration and oversight track initiated with the Equipment Leasing Registration Authority.' },
];

export const LEADERSHIP = [
  {
    slug: 'saadatu-musa-dangoggo-aliyu',
    name: 'Hajiya Sa’adatu Musa Dangoggo Aliyu',
    role: 'Board Chair',
    portrait: '/media/team/saadatu-aliyu.jpg',
    expertise: ['Real Estate & Construction', 'Project Management', 'Corporate Governance', 'Strategic Planning'],
    education: [
      'MSc Food Production & Management, University of Nottingham (UK)',
      'BSc Chemical Engineering, Budapest University of Technology and Economics',
      'Executive education at Harvard Business School, SPPG and Women on Board',
    ],
    currentRoles: ['Managing Director, Rural Homes Limited'],
    memberships: [
      'Nigerian Society of Engineers',
      'Council for Regulation of Engineering in Nigeria (COREN)',
      'National Institute of Management',
    ],
    bio: [
      'Hajiya Sa’adatu Musa Dangoggo Aliyu is an accomplished professional with strong expertise in real estate and construction, project management, corporate governance, and strategic planning.',
      'She holds advanced academic qualifications from the University of Nottingham (UK) and Budapest University of Technology and Economics, and has completed executive education at Harvard Business School, SPPG and Women on Board.',
      'Her career includes senior roles at NAFDAC (Corporate Governance), Urban Shelter Ltd and Ashburton Nigeria Ltd. She currently serves as Managing Director of Rural Homes Limited.',
    ],
  },
  {
    slug: 'abdullahi-mustapha',
    name: 'Abdullahi Mustapha',
    role: 'Chief Executive Officer',
    credentials: 'FNIQS, MCIPS, RQS, PMP',
    portrait: '/media/team/abdullahi-mustapha.jpeg',
    expertise: ['Quantity Surveying', 'Procurement', 'Logistics & Supply Chain', 'Asset Leasing', 'Construction'],
    education: [
      'MBA Finance and Investment — Ahmadu Bello University, Zaria, Nigeria',
      'MSc Procurement, Logistics and Supply Chain Management — University of Salford, Manchester, UK',
      'B.Tech Quantity Surveying — Federal University of Technology, Minna, Nigeria',
      'PMP — Project Management Professional',
    ],
    currentRoles: [
      'Chief Executive Officer, Easylease Global Resources Limited',
      'Chairman, Gianni and Vanni Limited',
      'Education & Professional Development Secretary, NIQS FCT Chapter',
    ],
    memberships: [
      'Fellow, Nigerian Institute of Quantity Surveyors (FNIQS)',
      'Registered Quantity Surveyor with QSRBN (RQS)',
      'Member, Chartered Institute of Procurement & Supply (MCIPS)',
      'Project Management Professional (PMP)',
    ],
    bio: [
      'Abdullahi Mustapha is a Quantity Surveyor, procurement and logistics professional, and business executive with multidisciplinary experience across construction, real estate development, supply chain management, and asset leasing.',
      'Under his leadership, EasyLease has developed a structured asset register, pricing framework, governance systems, and a growing client portfolio across private developers and contractors.',
      'He holds an MBA in Finance and Investment from Ahmadu Bello University (Zaria), an MSc in Procurement, Logistics and Supply Chain Management from the University of Salford (Manchester, UK), and a B.Tech in Quantity Surveying from the Federal University of Technology, Minna. He is a certified Project Management Professional (PMP), a Fellow of the Nigerian Institute of Quantity Surveyors (FNIQS), a Registered Quantity Surveyor with QSRBN (RQS), and a Member of the Chartered Institute of Procurement & Supply (MCIPS).',
    ],
  },
  {
    slug: 'ember-audu',
    name: 'Ember Audu',
    role: 'Non-Executive Director',
    credentials: 'Chair, Nomination, Governance, Remuneration & Compensation',
    portrait: '/media/team/ember-audu.jpg',
    expertise: ['Strategic Planning', 'Board Governance', 'Human Capital Development', 'Legislative Drafting', 'Chartered Secretariat'],
    education: [
      'LLB, Benue State University',
      'BL, Nigerian Law School',
      'MSc Management, University of Sunderland (UK)',
      'Executive education: INSEAD, Royal Military Academy Sandhurst, Nigeria Institute of Advanced Legal Studies, IP3, ASCON, MSBM',
    ],
    currentRoles: ['Chairperson, Larimar Hospitality Services'],
    memberships: ['Member, Aqua Pro Life', 'Member, SECHILD Sedoo Initiative', 'Member, EMPAWA'],
    bio: [
      'Ember Audu is a qualified lawyer, chartered secretariat and human-capital specialist with two decades of professional experience across the legal, legislative and corporate governance domains.',
      'She serves as Chairperson of Larimar Hospitality Services and has held senior roles at the Petroleum Equalization Fund (Operations, Legal and Client Relations) and as a Legislative Aide in the National Assembly.',
      'She chairs the Nomination, Governance, Remuneration & Compensation (NGRC) Committee of the EGRL Board.',
    ],
  },
  {
    slug: 'sadiq-bisallah',
    name: 'Sadiq I. I. Bisallah',
    role: 'Non-Executive Director',
    credentials: 'Chair, Public Sector & Stakeholder Engagement',
    portrait: '/media/team/sadiq-bisallah.jpg',
    expertise: ['Investment Portfolio Management', 'Business Development', 'Public-Private Partnerships', 'Development Policy & Economic Planning', 'Public Sector Engagement'],
    education: [
      'BSc Political Science (Political Economy)',
      'MSc Development Studies (Development Economics)',
      'MBA Finance and Investment',
      'Currently pursuing a PhD in Management',
    ],
    currentRoles: [
      'Head of Investment Services, Niger State Development Company Limited',
      'Chairperson Think-Tank Committee, Niger State Development Company Limited',
      'Non-Executive Director, Easylease Global Resources Limited',
    ],
    memberships: [
      'Chartered Member, Nigerian Institute of Management',
      'Member, Institute of Chartered Secretaries of Nigeria',
      'Member, Chartered Institute of Economists of Nigeria',
    ],
    bio: [
      'Sadiq I. I. Bisallah is a seasoned investment and business development professional with over 17 years of experience spanning the public and private sectors in Nigeria.',
      'He serves as Head of Investment Services and Chairperson of the Think-Tank Committee at Niger State Development Company Limited, and chairs the Public Sector & Stakeholder Engagement Sub-committee of the EGRL Board.',
      'His previous roles include Senior Special Assistant to the Governor of Niger State on Leadership Recruitment Process, and Resident Desk Officer at the Strategic Operations Unit (SOU) under the administration of Dr. Mu’azu Babangida Aliyu.',
    ],
  },
  {
    slug: 'gbenga-omoniyi',
    name: 'Gbenga Omoniyi',
    role: 'Non-Executive Director',
    credentials: 'Chair, Operations Strategy & Business Development',
    portrait: '/media/team/gbenga-omoniyi.jpg',
    expertise: ['Business Strategy', 'ICT & Digital Economy', 'Business Process Operations', 'Corporate Culture & Change Management', 'Project Management'],
    education: [
      'MBA',
      'Postgraduate Diplomas in Strategy & Management and Business Management',
      'DBA (in view)',
      'Executive education: Advanced Management Program, Lagos Business School',
    ],
    currentRoles: ['Consultant, IRIS / RBS Consulting'],
    memberships: [],
    bio: [
      'Gbenga Omoniyi is a business strategy and ICT professional with cross-sector experience spanning telecommunications (MTN, Galaxy Backbone), oil & gas (GPDC) and regional governance (ECOWAS).',
      'His expertise sits at the intersection of business strategy, digital economy and operational transformation — anchoring his chairmanship of the Operations Strategy & Business Development Committee of the EGRL Board.',
      'He is currently a consultant with IRIS / RBS Consulting.',
    ],
  },
  {
    slug: 'farida-balarabe',
    name: 'Farida Balarabe',
    role: 'Non-Executive Director',
    credentials: 'Chair, Risk, Audit & Compliance',
    portrait: '/media/team/farida-balarabe.webp',
    expertise: ['Corporate Governance', 'Strategic Planning', 'Operations Management', 'Human Capital Development', 'Project Management'],
    education: 'MSc Sociology (Social Psychology), Baze University Abuja (2024) • BSc Sociology & Anthropology, Baze University Abuja (2017) • Professional Diploma in Mass Communication, Bayero University Kano (1999). Certifications: Health Coach Institute (USA, 2024), Gaia Education (Scotland, 2022), North American Montessori Centre (2020).',
    currentRoles: [
      'Founder & CEO, Munchkin Bites Limited',
      'Co-Founder & Head of Mission, Neema Takeshi Foundation for Women & Children',
      'Non-Executive Director, Easylease Global Resources Limited',
    ],
    memberships: [
      'Member, Nigerian College of Forensics and Fraud Investigators (NICFFI)',
      'Member, Society for Corporate Governance Nigeria',
      'Associate Member, Women in Management, Business and Public Service (WIMBIZ)',
    ],
    bio: [
      'Farida Balarabe is an entrepreneur, governance professional, certified life and health coach, and social impact advocate with leadership experience spanning hospitality, education, retail, and medical tourism.',
      'She is the Founder and CEO of Munchkin Bites Limited, where she built Gardenia into a recognised restaurant in Abuja, and the Co-Founder of the Neema Takeshi Foundation for Women & Children.',
      'A former Trustee of the American International School of Abuja, she chairs the Risk, Audit & Compliance (RAC) Sub-committee of the EGRL Board.',
    ],
  },
];

export const MANAGEMENT = [
  {
    slug: 'ogundijo-victoria-omobola',
    name: 'Victoria Ogundijo',
    role: 'Company Accountant',
    portrait: '/media/team/victoria-ogundijo.jpeg',
    expertise: ['Financial Reporting & Compliance', 'Cash Flow & Budget Management', 'Asset & Inventory Control', 'ERP Accounting (QuickBooks, Sage)'],
    bio: [
      'Victoria is a result-driven accountant with over four years’ experience in financial reporting and compliance, cash flow and budget management, asset and inventory control, and ERP-based accounting on QuickBooks and Sage.',
      'She holds a BSc in Accounting and is ACA (ICAN) in view. Her role supports EGRL’s financial discipline, internal controls, and reporting integrity.',
    ],
  },
  {
    slug: 'habibullahi-abdulrahman',
    name: 'Habibullahi Abdulrahman',
    role: 'Technical Assistant to CEO',
    portrait: '/media/team/habibullahi-abdulrahman.jpeg',
    bio: ['Habibullahi supports the CEO’s office across commercial coordination, executive research, and project follow-through across the EGRL portfolio.'],
  },
  {
    slug: 'alih-muhammed',
    name: 'Alih Muhammed',
    role: 'Logistics Supervisor',
    portrait: '/media/team/alih-muhammed.jpeg',
    bio: ['Alih coordinates outbound convoys, third-party haulage, and asset mobilisation across active EGRL sites in the FCT and neighbouring states.'],
  },
  {
    slug: 'kish-luka',
    name: 'Kish Luka',
    role: 'Inventory Management Officer',
    portrait: '/media/team/kish-luka.jpeg',
    bio: ['Kish owns the daily asset movement register, reconciles physical inventory against lease contracts, and manages the preventive-maintenance calendar for the yard.'],
  },
  {
    slug: 'sunday-agada',
    name: 'Sunday Agada',
    role: 'Foreman',
    portrait: '/media/team/sunday-agada.jpeg',
    bio: ['Sunday leads on-site scaffold erection and dismantling crews, enforcing PPE compliance and safe rigging standards on active EGRL projects.'],
  },
  {
    slug: 'mohammed-salih-jankaro',
    name: 'Mohammed Salih Jankaro',
    role: 'Driver',
    portrait: '/media/team/mohammed-salih-jankaro.jpeg',
    bio: ['Mohammed handles asset transport and on-schedule delivery of scaffolding, formworks and containerised units to client sites across Nigeria.'],
  },
];

export const GOVERNANCE_COMMITTEES = [
  { title: 'Risk, Audit & Compliance', body: 'Independent oversight over risk posture, external audit and regulatory compliance. Chaired by Farida Balarabe.' },
  { title: 'Operations Strategy & Business Development', body: 'Reviews commercial pipeline, asset build-out and market expansion plans. Chaired by Gbenga Omoniyi.' },
  { title: 'Public Sector & Stakeholder Engagement', body: 'Coordinates institutional relationships and public-sector engagement. Chaired by Sadiq I. I. Bisallah.' },
  { title: 'Nomination, Governance, Remuneration & Compensation', body: 'Board composition, succession planning and executive remuneration governance. Chaired by Ember Audu.' },
];

export const WHY_CHOOSE_US = [
  { title: 'Board-led governance', body: 'A permanent, independent board with five dedicated committees — a discipline usually reserved for mature corporates.' },
  { title: 'Credentialed leadership', body: 'CEO is a Fellow of the Nigerian Institute of Quantity Surveyors (FNIQS), an RQS with QSRBN, and MCIPS certified.' },
  { title: 'Structured asset lifecycle', body: 'Every asset lives inside a formal register, preventive-maintenance calendar and inspection log.' },
  { title: 'Operating-lease flexibility', body: 'Assets stay on our balance sheet. You pay for use — aligned to project timelines and cash-flow.' },
  { title: 'Compliance-first posture', body: 'Client-default procedures, insurance frameworks and an ELRA registration track already in motion.' },
  { title: 'Nigerian by design', body: 'Engineered for Nigerian site conditions, regulatory context and construction culture.' },
];

export const PROJECT_CATEGORIES = [
  { key: 'all', label: 'All Assets' },
  { key: 'scaffold', label: 'Scaffold' },
  { key: 'acrow', label: 'Acrow Props' },
  { key: 'columns', label: 'Metal Columns' },
  { key: 'formworks', label: 'Formworks' },
  { key: 'containers', label: 'Containers' },
];

// Careers page hidden per client instruction. Projects page hidden (Gallery covers it). Kept out of main nav.
export const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/leadership', label: 'Leadership' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/news', label: 'Insights' },
  { to: '/contact', label: 'Contact' },
];
